import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  RadioGroup, FormControlLabel, Radio, Typography, CircularProgress, Box, Snackbar, Alert,
} from '@mui/material';
import { buscarEmailsPadraoNotificacaoCobranca } from '../../../../services/core/processos/processosEnviarEmailDevedorService';

const MENSAGEM_ERRO_CARREGAR = 'Não foi possível carregar os modelos de e-mail.';

/**
 * Porta de `EmailsPadraoDialog.jsx` (tela real, modo `isAcaoEmMassa` — único
 * relevante aqui), Etapa 3 de `docs/architecture/plano-acoes-em-massa-grid-processos.md`.
 * Sub-dialog de seleção de modelo de e-mail — pré-requisito da Etapa 4
 * (`DialogEnviarEmailDevedor.jsx`), que vai consumi-lo via `onConfirmar`.
 *
 * Diferença deliberada da tela real: em vez de duas callbacks moldadas no
 * `formFields` (`FieldModel`) da tela real (`onChangeTexto` + `setFormFields`),
 * expõe uma única `onConfirmar(modelo)` com o objeto já pronto — mais simples
 * de consumir a partir de `useState` local (protótipo não usa `FieldModel`,
 * ver `src/prototipo/CLAUDE.md`). `nomeModelo` usa `modelo.nomeTipo` (nome
 * real do modelo) em vez da chave composta interna usada só para amarrar a
 * seleção do radio — a tela real usa essa chave composta também como
 * `nomeModelo`, o que parece um descuido lá; aqui optou-se por reportar o
 * nome de fato, que é o que a Etapa 4/critério do plano pede ("nome do
 * modelo").
 */
export default function EmailsPadraoDialog({ open, onClose, onConfirmar }) {
  const [carregando, setCarregando] = useState(false);
  const [modelos, setModelos] = useState([]);
  const [selecionado, setSelecionado] = useState('');
  const [erroGenerico, setErroGenerico] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let ignore = false;
    setSelecionado('');
    setErroGenerico('');
    setCarregando(true);
    buscarEmailsPadraoNotificacaoCobranca()
      .then((response) => { if (!ignore) setModelos(response.data); })
      .catch(() => { if (!ignore) { setModelos([]); setErroGenerico(MENSAGEM_ERRO_CARREGAR); } })
      .finally(() => { if (!ignore) setCarregando(false); });
    return () => { ignore = true; };
  }, [open]);

  function onConfirmarClick() {
    const modelo = modelos[Number(selecionado)];
    if (!modelo) return;
    onConfirmar({
      texto: modelo.textoEmail,
      assunto: modelo.assuntoEmail,
      idTipoEmailPadrao: modelo.idTipoEmailPadrao,
      solicitarFase: modelo.solicitarFase,
      nomeModelo: modelo.nomeTipo,
    });
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Emails Padrão</DialogTitle>
      <DialogContent>
        <Typography id="emails-padrao-label" variant="body2" sx={{ mb: 1 }}>
          Selecione um E-mail Padrão:
        </Typography>
        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <RadioGroup
            aria-labelledby="emails-padrao-label"
            value={selecionado}
            onChange={event => setSelecionado(event.target.value)}
          >
            {modelos.map((modelo, indice) => (
              <FormControlLabel
                // eslint-disable-next-line react/no-array-index-key
                key={`${modelo.assuntoEmail}-${modelo.nomeTipo}-${indice}`}
                value={String(indice)}
                control={<Radio size="small" />}
                label={modelo.nomeTipo}
              />
            ))}
            {!modelos.length && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Nenhum modelo de e-mail disponível.
              </Typography>
            )}
          </RadioGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!selecionado} onClick={onConfirmarClick}>Confirmar</Button>
      </DialogActions>

      <Snackbar
        open={Boolean(erroGenerico)}
        autoHideDuration={4000}
        onClose={() => setErroGenerico('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErroGenerico('')}>
          {erroGenerico}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

EmailsPadraoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirmar: PropTypes.func.isRequired,
};
